import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useProducts } from '../context/ProductContext';
import aiChatService from '../services/aiChatService';
import whatsappIntegration from '../utils/whatsappIntegration';
import {
  MessageCircle,
  X,
  Send,
  Bot,
  User,
  Phone,
  Mail,
  Clock,
  CheckCircle,
  Minimize2,
  Maximize2,
  MessageSquare,
  Headphones
} from 'lucide-react';

const ChatWidget = () => {
  const { user } = useAuth();
  const { settings } = useSiteSettings();
  const { products, categories } = useProducts();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeTab, setActiveTab] = useState('ai'); // 'ai', 'support', 'whatsapp'
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // Load product data into AI service
  useEffect(() => {
    if (products && products.length > 0) {
      aiChatService.loadProductData(products, categories);
      console.log(`🤖 AI trained with ${products.length} products`);
    }
  }, [products, categories]);

  // Initial AI greeting
  useEffect(() => {
    if (messages.length === 0) {
      setMessages([
        {
          id: 1,
          type: 'ai',
          message: `Hello${user ? ` ${user.displayName || 'there'}` : ''}! 👋 I'm your AI assistant for Gifted Solutions. I'm trained on our ${products?.length || 'latest'} products and ready to help you find exactly what you need!`,
          timestamp: new Date(),
          suggestions: [
            'Show me Arduino boards',
            'What sensors do you have?',
            'Help with my project',
            'Check product prices'
          ]
        }
      ]);
    }
  }, [user, products]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest'
      });
    }
  };

  // AI Response using enhanced AI service
  const getAIResponse = async (message) => {
    setIsTyping(true);
    setIsLoading(true);

    try {
      // Simulate realistic response time
      await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 1000));

      // Get AI response from service
      const response = await aiChatService.generateResponse(message, {
        user: user,
        conversationHistory: messages
      });

      // Get suggestions for follow-up
      const suggestions = aiChatService.getSuggestions();

      setIsTyping(false);
      setIsLoading(false);

      return {
        id: Date.now(),
        type: 'ai',
        message: response,
        timestamp: new Date(),
        suggestions: suggestions
      };
    } catch (error) {
      console.error('AI Response Error:', error);
      setIsTyping(false);
      setIsLoading(false);

      return {
        id: Date.now(),
        type: 'ai',
        message: "🤖 I'm experiencing some technical difficulties. Please try again or contact our support team for immediate assistance.",
        timestamp: new Date()
      };
    }
  };

  const handleSendMessage = async (messageText = inputMessage) => {
    if (!messageText.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      message: messageText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');

    if (activeTab === 'ai') {
      const aiResponse = await getAIResponse(messageText);
      setMessages(prev => [...prev, aiResponse]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    handleSendMessage(suggestion);
  };

  const openWhatsApp = () => {
    const userInfo = {
      name: user?.displayName || '',
      email: user?.email || ''
    };

    const message = whatsappIntegration.generateSupportMessage(
      userInfo,
      'general',
      'I need assistance with your products and services.'
    );

    whatsappIntegration.openChat(
      settings?.whatsappNumber || null,
      message,
      { issueType: 'general' }
    );
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsOpen(true)}
          className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 animate-pulse"
        >
          <MessageCircle size={24} />
        </button>
        <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center animate-bounce">
          AI
        </div>
      </div>
    );
  }

  return (
    <div className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
      isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]'
    }`}>
      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <MessageCircle size={20} />
            <span className="font-semibold">
              {isMinimized ? 'Chat Support' : 'Gifted Solutions Support'}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="p-1 hover:bg-white/20 rounded transition-colors"
            >
              {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-white/20 rounded transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Tabs */}
            <div className="flex border-b border-gray-200">
              <button
                onClick={() => setActiveTab('ai')}
                className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center space-x-2 transition-colors ${
                  activeTab === 'ai'
                    ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Bot size={16} />
                <span>AI Chat</span>
              </button>
              <button
                onClick={() => setActiveTab('support')}
                className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center space-x-2 transition-colors ${
                  activeTab === 'support'
                    ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Headphones size={16} />
                <span>Support</span>
              </button>
              <button
                onClick={() => setActiveTab('whatsapp')}
                className={`flex-1 py-3 px-4 text-sm font-medium flex items-center justify-center space-x-2 transition-colors ${
                  activeTab === 'whatsapp'
                    ? 'bg-green-50 text-green-600 border-b-2 border-green-600'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Phone size={16} />
                <span>WhatsApp</span>
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 flex flex-col">
              {activeTab === 'ai' && (
                <>
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-96 scroll-smooth">
                    {messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg p-3 ${
                            msg.type === 'user'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          <div className="flex items-start space-x-2">
                            {msg.type === 'ai' && (
                              <Bot size={16} className="text-blue-600 mt-1 flex-shrink-0" />
                            )}
                            <div className="flex-1">
                              <p className="text-sm whitespace-pre-line">{msg.message}</p>
                              <p className={`text-xs mt-1 ${
                                msg.type === 'user' ? 'text-blue-100' : 'text-gray-500'
                              }`}>
                                {formatTime(msg.timestamp)}
                              </p>
                            </div>
                          </div>
                          {msg.suggestions && (
                            <div className="mt-3 space-y-2">
                              {msg.suggestions.map((suggestion, index) => (
                                <button
                                  key={index}
                                  onClick={() => handleSuggestionClick(suggestion)}
                                  className="block w-full text-left text-xs bg-white/20 hover:bg-white/30 rounded px-2 py-1 transition-colors"
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {isTyping && (
                      <div className="flex justify-start">
                        <div className="bg-gray-100 rounded-lg p-3 flex items-center space-x-2">
                          <Bot size={16} className="text-blue-600" />
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                          </div>
                        </div>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  <div className="border-t border-gray-200 p-4">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleSendMessage();
                      }}
                      className="flex space-x-2"
                    >
                      <input
                        type="text"
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        placeholder="Type your message..."
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        disabled={isLoading}
                      />
                      <button
                        type="submit"
                        disabled={!inputMessage.trim() || isLoading}
                        className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <Send size={16} />
                      </button>
                    </form>
                  </div>
                </>
              )}

              {activeTab === 'support' && (
                <div className="flex-1 p-6 flex flex-col justify-center items-center text-center">
                  <Headphones size={48} className="text-blue-600 mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Contact Support</h3>
                  <p className="text-gray-600 mb-6">Get help from our support team</p>
                  
                  <div className="space-y-4 w-full">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-2">
                        <Mail className="text-blue-600" size={20} />
                        <span className="font-medium">Email Support</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">Get detailed help via email</p>
                      <a
                        href={`mailto:${settings?.email || 'support@giftedsolutions.com'}?subject=Support Request&body=Hello, I need help with:`}
                        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors text-sm inline-block"
                      >
                        Send Email
                      </a>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center space-x-3 mb-2">
                        <Phone className="text-green-600" size={20} />
                        <span className="font-medium">Phone Support</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">Call us directly for immediate help</p>
                      <a
                        href={`tel:${settings?.phone1 || '+1234567890'}`}
                        className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors text-sm inline-block"
                      >
                        Call Now
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'whatsapp' && (
                <div className="flex-1 p-6 flex flex-col justify-center items-center text-center">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mb-4">
                    <MessageSquare size={32} className="text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">WhatsApp Support</h3>
                  <p className="text-gray-600 mb-6">Chat with us instantly on WhatsApp</p>
                  
                  <div className="bg-green-50 rounded-lg p-4 mb-6 w-full">
                    <div className="flex items-center space-x-2 mb-2">
                      <CheckCircle className="text-green-600" size={16} />
                      <span className="text-sm font-medium text-green-800">Instant responses</span>
                    </div>
                    <div className="flex items-center space-x-2 mb-2">
                      <CheckCircle className="text-green-600" size={16} />
                      <span className="text-sm font-medium text-green-800">Share images & files</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="text-green-600" size={16} />
                      <span className="text-sm font-medium text-green-800">Available 24/7</span>
                    </div>
                  </div>

                  <button
                    onClick={openWhatsApp}
                    className="w-full bg-green-500 text-white py-3 px-6 rounded-lg hover:bg-green-600 transition-colors font-medium flex items-center justify-center space-x-2"
                  >
                    <MessageSquare size={20} />
                    <span>Open WhatsApp Chat</span>
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatWidget;
